/**
 * Loaders for bid details: loadNotes, loadTemplateData.
 * Full keeps loadBidDetails and the initial load effect (calls loadBidDetails + loadNotes or loadTemplateData).
 * Hook receives state from useBidAnalysisState and returns loadNotes, loadTemplateData for use in Full and in effects.
 */
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from 'hooks/useStore';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';

export type BidDetailsLoadStateBag = {
  setLoading: (v: boolean) => void;
  setTemplateData: (v: any) => void;
  setAnalysisType: (v: string) => void;
  setGsmsHndlgType: (v: string) => void;
  setPricingValidatedChecked: (v: boolean) => void;
  setFormData: (v: any) => void;
  setPackSizeFormData: (v: any) => void;
  setIsLoadingNotes: (v: boolean) => void;
  setNotes: (v: any) => void;
};

export function useBidDetailsLoad(state: BidDetailsLoadStateBag) {
  const { bidId } = useParams<{ bidId: string }>();
  const { BidAnalysisStore } = useStore();

  const {
    setLoading,
    setTemplateData,
    setAnalysisType,
    setGsmsHndlgType,
    setPricingValidatedChecked,
    setFormData,
    setPackSizeFormData,
    setIsLoadingNotes,
    setNotes,
  } = state;

  const loadNotes = useCallback(async () => {
    if (!bidId) return;
    setIsLoadingNotes(true);
    try {
      const notesData = await bidAnalysisServices.getNotes(parseInt(bidId));
      setNotes(notesData);
    } catch (error) {
      console.error('Error loading notes:', error);
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [bidId, setIsLoadingNotes, setNotes]);

  const loadTemplateData = useCallback(async () => {
    setLoading(true);
    try {
      const bidDetails = BidAnalysisStore.currentBidDetails;
      const productTypeForTemplate = bidDetails?.product_type || 'direct';
      const data = await bidAnalysisServices.getTemplateData(productTypeForTemplate);
      setTemplateData(data);
      setAnalysisType(data.projectInformation?.analysisType ?? 'New Launch');
      setGsmsHndlgType(data.projectInformation?.gsmsHndlgType || '');
      setPricingValidatedChecked(data.projectInformation?.pricingApprovedByPnC ?? false);

      const initialData: any = {
        ...data.cogsBuildUp,
      };

      if (productTypeForTemplate === 'indirect') {
        initialData.gsmsHndlgType = 'Marketing Income';
        setGsmsHndlgType('Marketing Income');
      }

      setFormData(initialData);
      setPackSizeFormData({
        0: initialData,
      });
    } catch (error) {
      console.error('Error loading template data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setLoading,
    setTemplateData,
    setAnalysisType,
    setGsmsHndlgType,
    setPricingValidatedChecked,
    setFormData,
    setPackSizeFormData,
  ]);

  return { loadNotes, loadTemplateData };
}
